import axios from "axios";

export type MapEditorAddressLookupType =
  | string
  | {
      street: string;
      postalCode: string;
      city: string;
      country: string;
    };

const NOMINATIM = "https://nominatim.openstreetmap.org/search";

function coordsFromHit(data: { lat: string; lon: string }) {
  return {
    Lat: Number(data.lat),
    Lng: Number(data.lon),
  };
}

/** Skip empty lookups and useless Nominatim calls (MapEditor uses this before fetching). */
export function isAddressLookupReady(
  address: MapEditorAddressLookupType | undefined
): boolean {
  if (address == null) {
    return false;
  }
  if (typeof address === "string") {
    return address.trim().length > 0;
  }
  const { street, postalCode, city, country } = address;
  const hasLocation =
    !!street?.trim() || !!city?.trim() || !!postalCode?.trim();
  const hasCountry = !!country?.trim();
  return (
    hasLocation &&
    (hasCountry || (!!street?.trim() && !!city?.trim() && !!postalCode?.trim()))
  );
}

async function fetchFirstHit(
  url: string,
  headers: Record<string, string>
): Promise<{ Lat: number; Lng: number } | null> {
  try {
    const json = await axios.get<{ lat: string; lon: string }[]>(url, {
      headers,
    });
    if (json.data?.length > 0) {
      return coordsFromHit(json.data[0]);
    }
  } catch {
    /* network / CORS */
  }
  return null;
}

export const useMapEditorAddressLookup = () => {
  async function LookUpAddress(address: MapEditorAddressLookupType) {
    const headers = { Accept: "application/json" };

    if (typeof address === "string") {
      const trimmed = address.trim();
      if (!trimmed) {
        return null;
      }

      const urls: string[] = [
        `${NOMINATIM}?format=json&limit=1&q=${encodeURIComponent(trimmed)}`,
      ];

      /** Wrong ISO on form (e.g. Bilzen + ES) → drop trailing ", XX" and retry. */
      const withoutTrailingIso = trimmed
        .replace(/,\s*[A-Za-z]{2}\s*$/, "")
        .trim();
      if (withoutTrailingIso !== trimmed && withoutTrailingIso.length > 0) {
        urls.push(
          `${NOMINATIM}?format=json&limit=1&q=${encodeURIComponent(withoutTrailingIso)}`
        );
      }

      const seen = new Set<string>();
      for (const url of urls) {
        if (seen.has(url)) {
          continue;
        }
        seen.add(url);
        const hit = await fetchFirstHit(url, headers);
        if (hit) {
          return hit;
        }
      }
      return null;
    }

    const street = address.street?.trim() ?? "";
    const postalCode = address.postalCode?.trim() ?? "";
    const city = address.city?.trim() ?? "";
    const country = address.country?.trim() ?? "";

    const urls: string[] = [];

    if (street && city && postalCode && country) {
      urls.push(
        `${NOMINATIM}?format=json&limit=1&street=${encodeURIComponent(
          street
        )}&city=${encodeURIComponent(city)}&postalcode=${encodeURIComponent(
          postalCode
        )}&country=${encodeURIComponent(country)}`
      );
    }

    const fullLine = [street, postalCode, city, country]
      .filter((s) => s.length > 0)
      .join(", ");
    if (fullLine) {
      urls.push(
        `${NOMINATIM}?format=json&limit=1&q=${encodeURIComponent(fullLine)}`
      );
    }

    /** Critical when country code is wrong (e.g. tenant default ≠ destination). */
    const withoutCountry = [street, postalCode, city]
      .filter((s) => s.length > 0)
      .join(", ");
    if (withoutCountry) {
      urls.push(
        `${NOMINATIM}?format=json&limit=1&q=${encodeURIComponent(withoutCountry)}`
      );
    }

    if (postalCode && city) {
      urls.push(
        `${NOMINATIM}?format=json&limit=1&q=${encodeURIComponent(
          `${postalCode} ${city}`
        )}`
      );
    }

    if (street && city) {
      urls.push(
        `${NOMINATIM}?format=json&limit=1&q=${encodeURIComponent(
          `${street}, ${city}`
        )}`
      );
    }

    const seen = new Set<string>();
    for (const url of urls) {
      if (seen.has(url)) {
        continue;
      }
      seen.add(url);
      const hit = await fetchFirstHit(url, headers);
      if (hit) {
        return hit;
      }
    }
    return null;
  }

  return {
    LookUpAddress,
  };
};
